import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { defineMessages } from 'react-intl';

import { FlyoutContext } from 'components/Flyout/FlyoutContext';
import { ICONS } from 'constants/icons';
import LINKS from 'constants/links';
import { reactHookFormPropTypes } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import ClassLevelMinimumSpotsContext from '../Contexts/ClassLevelMinimumSpotsContext';
import ClassLevelConfigureSection from './ClassLevelConfigureSection';
import ClassLevelMinimumSpotsFlyout from './ClassLevelMinimumSpotsFlyout';
import MinimumSpotsItem from './MinimumSpotsItem';
import SmartToolsSection from './SmartToolsSection';

export const messages = defineMessages({
  title: {
    id: 'smart-tools.minimum-spots.title',
    defaultMessage: 'Always ensure there are a certain amount of spots bookable to ClassPass members ',
  },
  description: {
    id: 'smart-tools.minimum-spots.description',
    defaultMessage:
      'This is the starting allocation we’ll keep open for ClassPass members. SmartSpot may allocate more, but never less than this number.  <a>Learn more</a>',
  },
  settingDetail: {
    id: 'smart-tools.minimum-spots.setting-detail',
    defaultMessage: 'This setting works with the default SmartSpot algorithm to help attract customers.',
  },
  peak: {
    id: 'smart-tools.minimum-spots.peak',
    defaultMessage: 'Set the starting allocation for Peak times',
  },
  peakInfo: {
    id: 'smart-tools.minimum-spots.peak-info',
    defaultMessage: '3 is a common allocation for peak times at studios with similar capacity.',
  },
  offPeak: {
    id: 'smart-tools.minimum-spots.off-peak',
    defaultMessage: 'Set the starting allocation for Off-peak times',
  },
  offPeakInfo: {
    id: 'smart-tools.minimum-spots.off-peak-info',
    defaultMessage: '5 is a common allocation for off-peak times at studios with similar capacity.',
  },
  select: {
    id: 'smart-tools.minimum-spots.select',
    defaultMessage: 'Select',
  },
  classLevel: {
    id: 'smart-tools.minimum-spots.class-level',
    defaultMessage: 'Class-level',
  },
  venueLevel: {
    id: 'smart-tools.minimum-spots.venue-level',
    defaultMessage: 'Venue-level',
  },
  configure: {
    id: 'smart-tools.minimum-spots.class-level-flyout.configure',
    defaultMessage: 'Configure',
  },
  configureTitle: {
    id: 'smart-tools.minimum-spots.class-level-flyout.configure-title',
    defaultMessage: 'Set the starting allocation for individual classes',
  },
  configureDescription: {
    id: 'smart-tools.minimum-spots.class-level-flyout.configure-description',
    defaultMessage: 'Set the starting allocation for individual classes for peak and off-peak times.',
  },
});

const { element } = bem('minimum-spots');
const CLASS_LEVEL_FLYOUT_ID = 'class-level-minimum-spots-flyout';

const MinimumSpots = ({
  control,
  countryCode = 'USA',
  handleLearnMore,
  showSelectOptionPeakTime,
  showSelectOptionOffPeakTime,
}) => {
  const TABS = { VENUE: 'venue-level', CLASS: 'class-level' };

  const [selectedLevel, setSelectedLevel] = useState(TABS.VENUE);

  const handleTabChange = tabName => {
    setSelectedLevel(tabName);
  };

  const TABS_LIST = [
    { name: TABS.VENUE, label: messages.venueLevel },
    { name: TABS.CLASS, label: messages.classLevel },
  ];

  const { openFlyout } = useContext(FlyoutContext);
  const { fetchData } = useContext(ClassLevelMinimumSpotsContext);

  const handleConfigureClick = () => {
    fetchData();
    openFlyout(CLASS_LEVEL_FLYOUT_ID);
  };

  return (
    <>
      <SmartToolsSection
        title={messages.title}
        description={messages.description}
        settingDetail={messages.settingDetail}
        icon={ICONS.members}
        countryCode={countryCode}
        learnMoreLink={LINKS.smartspot.learnMore}
        handleLearnMore={handleLearnMore}
        learnMoreLocation="minimum_spots"
        tabs={TABS_LIST}
        selectedTab={selectedLevel}
        onTabChange={handleTabChange}
        tabsClassName={element('tabs')}>
        {selectedLevel === TABS.VENUE ? (
          <div className={element('content-group')}>
            <MinimumSpotsItem
              className={element('minimum-spots-item')}
              control={control}
              label={messages.peak}
              info={messages.peakInfo}
              name="minimumSpotsPeakTime"
              showSelectOption={showSelectOptionPeakTime}
            />
            <MinimumSpotsItem
              className={element('minimum-spots-item')}
              control={control}
              label={messages.offPeak}
              info={messages.offPeakInfo}
              name="minimumSpotsOffpeakTime"
              showSelectOption={showSelectOptionOffPeakTime}
            />
          </div>
        ) : (
          <ClassLevelConfigureSection
            onConfigureClick={handleConfigureClick}
            configureTitle={messages.configureTitle}
            configureDescription={messages.configureDescription}
            configure={messages.configure}
          />
        )}
      </SmartToolsSection>
      <ClassLevelMinimumSpotsFlyout flyoutId={CLASS_LEVEL_FLYOUT_ID} countryCode={countryCode} />
    </>
  );
};

MinimumSpots.propTypes = {
  control: reactHookFormPropTypes.control.isRequired,
  countryCode: PropTypes.string,
  handleLearnMore: PropTypes.func.isRequired,
  showSelectOptionPeakTime: PropTypes.bool.isRequired,
  showSelectOptionOffPeakTime: PropTypes.bool.isRequired,
};

export default MinimumSpots;
